import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, CheckCircle2, AlertTriangle, Lock, Download, ArrowRight, Loader2, Globe, ShieldAlert } from 'lucide-react';
import { getRef } from '../lib/referral';
import { trackEvent } from '../lib/analytics';
import { ShareEarnBar } from './ShareEarnBar';
import { HumanCheck } from './HumanCheck';
import { useT } from '../i18n';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  en: {
    eyebrow: 'FREE RESTAURANT SCAN · 30 SECONDS',
    title: 'Right now, someone is asking AI where to eat. Is the answer you — or your competitor?',
    body: "Every day, guests ask ChatGPT, Gemini and Google AI where to eat — and get ONE recommendation. If your restaurant isn't structured for AI, you're invisible in that answer, and the table goes to someone else. Scan your website live and see exactly where you're losing guests — free, in under 30 seconds.",
    placeholder: 'yourrestaurant.com',
    cta: 'Scan Free',
    scanning: 'Scanning',
    onlyRestaurants: 'Built for restaurant websites — enter your domain (e.g. yourrestaurant.com).',
    invalid: 'We could not reach that website. Check the spelling and try again (e.g. without "www").',
    blocked: 'This website has a firewall that blocks automated scans. Book a free consultation and we will audit it manually.',
    blockedTitle: 'Firewall detected — we will audit it manually, free',
    blockedBody: 'This website blocks automated scanners (common with Cloudflare). Leave your email and a Kitxens specialist will audit it by hand within 24-48h — full report, no cost.',
    blockedEmail: 'Your email', blockedName: 'Restaurant name',
    blockedCta: 'Get my free manual audit', blockedSending: 'Sending…',
    blockedDoneT: 'Got it! Your manual audit is in the queue 🎯',
    blockedDone: 'A specialist will email your full report within 24-48h. In a hurry?',
    verdictGood: 'Excellent', verdictMid: 'Needs Improvement', verdictBad: 'Critical',
    issues: (n) => `${n} issues detected on your website`,
    lockedTitle: "See exactly where you're losing guests — free",
    lockedBody: 'Every issue we found, the exact fixes, and your PDF report. The restaurants that fix this first are the ones AI recommends — the rest get skipped.',
    email: 'Work email', restaurant: 'Restaurant name', phone: 'Phone (optional)',
    unlock: 'Unlock Free Report', unlocking: 'Unlocking…',
    pdf: 'Download PDF report',
    fullTitle: 'Your complete diagnosis',
    upsellTitle: "Every week you wait, AI keeps recommending someone else. Let's fix that.",
    upsellBody: "A Kitxens specialist will walk you through your report and the exact plan to become the restaurant AI recommends — before your competitor does. 15 minutes, free, no strings.",
    upsellCta: 'Talk to an expert now — free',
    upsellSoon: 'Full written audit — available soon',
    prelimT: 'This is a preliminary report',
    prelim: "This automated scan is just the surface. Real visibility goes far beyond AI: technical & local SEO, GEO (generative engine optimization), structured Schemas, Google listings, reviews, speed and conversion. The full Kitxens audit covers all of it with a prioritized plan.",
    statChecks: 'checks run', statPass: 'passed', statIssues: 'to improve',
    emailed: 'We also sent this report to your email (check spam) with your PDF included.',
    wa: 'Chat with Rachel on WhatsApp',
    discount: '🎁 Book your demo this week and get 20% off your first month — mention code SCAN20.',
    fix: 'Fix',
    steps: ['Website', 'Robots.txt', 'Sitemap', 'Menu', 'Schema.org', 'Google signals', 'AI Search', 'Images', 'Catering', 'Local SEO', 'Mobile'],
  },
  es: {
    eyebrow: 'ESCANEO GRATUITO · 30 SEGUNDOS',
    title: 'Ahora mismo alguien pregunta a la IA dónde comer. ¿La respuesta eres tú — o tu competencia?',
    body: 'Cada día, los clientes preguntan a ChatGPT, Gemini y Google AI dónde comer — y reciben UNA recomendación. Si tu restaurante no está estructurado para la IA, eres invisible en esa respuesta y la mesa se la lleva otro. Escanea tu web en vivo y ve exactamente dónde estás perdiendo clientes — gratis, en menos de 30 segundos.',
    placeholder: 'turestaurante.com',
    cta: 'Escanear Gratis',
    scanning: 'Escaneando',
    onlyRestaurants: 'Pensado para webs de restaurantes — escribe tu dominio (ej: turestaurante.com).',
    invalid: 'No pudimos acceder a esa web. Revisa que esté bien escrita e inténtalo de nuevo (ej: sin "www").',
    blocked: 'Esta web tiene un firewall que bloquea escaneos automáticos. Agenda una consultoría gratis y la auditamos manualmente.',
    blockedTitle: 'Firewall detectado — te la auditamos a mano, gratis',
    blockedBody: 'Esta web bloquea escáneres automáticos (habitual con Cloudflare). Déjanos tu email y un especialista de Kitxens la audita manualmente en 24-48h — informe completo, sin coste.',
    blockedEmail: 'Tu email', blockedName: 'Nombre del restaurante',
    blockedCta: 'Quiero mi auditoría manual gratis', blockedSending: 'Enviando…',
    blockedDoneT: '¡Recibido! Tu auditoría manual está en cola 🎯',
    blockedDone: 'Un especialista te enviará el informe completo en 24-48h. ¿Tienes prisa?',
    verdictGood: 'Excelente', verdictMid: 'Necesita Mejoras', verdictBad: 'Crítico',
    issues: (n) => `${n} problemas detectados en tu web`,
    lockedTitle: 'Ve exactamente dónde estás perdiendo clientes — gratis',
    lockedBody: 'Cada problema encontrado, las soluciones exactas y tu informe PDF. Los restaurantes que lo arreglan primero son los que la IA recomienda — al resto los salta.',
    email: 'Email de trabajo', restaurant: 'Nombre del restaurante', phone: 'Teléfono (opcional)',
    unlock: 'Desbloquear Informe Gratis', unlocking: 'Desbloqueando…',
    pdf: 'Descargar informe PDF',
    fullTitle: 'Tu diagnóstico completo',
    upsellTitle: 'Cada semana que esperas, la IA sigue recomendando a otro. Arreglémoslo.',
    upsellBody: 'Un especialista de Kitxens repasará contigo tu informe y el plan exacto para ser el restaurante que la IA recomienda — antes de que lo haga tu competencia. 15 minutos, gratis, sin compromiso.',
    upsellCta: 'Habla con un experto ahora — gratis',
    upsellSoon: 'Auditoría escrita completa — disponible pronto',
    fix: 'Solución',
    steps: ['Sitio web', 'Robots.txt', 'Sitemap', 'Menú', 'Schema.org', 'Señales de Google', 'Búsqueda IA', 'Imágenes', 'Catering', 'SEO Local', 'Móvil'],
  },
};

function ScoreRing({ score }) {
  const R = 64, CIRC = 2 * Math.PI * R;
  const color = score >= 80 ? '#10b981' : score >= 55 ? '#F77F11' : '#ef4444';
  return (
    <div className="relative w-40 h-40" data-testid="scan-score-ring">
      <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
        <circle cx="80" cy="80" r={R} fill="none" stroke="#f1f1f1" strokeWidth="12" />
        <motion.circle cx="80" cy="80" r={R} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={CIRC} initial={{ strokeDashoffset: CIRC }}
          animate={{ strokeDashoffset: CIRC * (1 - score / 100) }} transition={{ duration: 1.4, ease: 'easeOut' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="text-[42px] font-bold leading-none tracking-[-0.03em]" style={{ color }}>{score}</p>
        <p className="text-[11px] font-bold text-neutral-400">/ 100</p>
      </div>
    </div>
  );
}

function CategoryBars({ data, isEs }) {
  return (
    <div className="w-full space-y-3.5">
      {Object.entries(data.cat_labels).map(([key, meta]) => {
        const val = data.categories[key] ?? 0;
        const pct = (val / meta.max) * 100;
        const color = pct >= 70 ? '#10b981' : pct >= 45 ? '#F77F11' : '#ef4444';
        return (
          <div key={key} data-testid={`scan-cat-${key}`}>
            <div className="flex items-baseline justify-between">
              <p className="text-[13px] font-semibold text-neutral-800">{isEs ? meta.es : meta.en}</p>
              <p className="text-[12.5px] font-bold tabular-nums" style={{ color }}>{val} <span className="text-neutral-400 font-medium">/ {meta.max}</span></p>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-neutral-100 overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, ease: 'easeOut' }}
                className="h-full rounded-full" style={{ backgroundColor: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CheckRow({ c, isEs, showFix }) {
  return (
    <div className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 ${c.ok ? 'border-emerald-100 bg-emerald-50/40' : 'border-amber-200/70 bg-amber-50/50'}`}>
      {c.ok
        ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
        : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />}
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-neutral-800 leading-snug">{isEs ? c.label_es : c.label}</p>
        {showFix && !c.ok && (c.fix || c.fix_es) && (
          <p className="mt-0.5 text-[12px] text-neutral-500 leading-snug">{isEs ? c.fix_es : c.fix}</p>
        )}
      </div>
    </div>
  );
}

export default function IndexAiScanner() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const t = T[isEs ? 'es' : 'en'];
  const [url, setUrl] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | scanning | preview | unlocked
  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState('');
  const [blockedDomain, setBlockedDomain] = useState('');
  const [rescue, setRescue] = useState({ email: '', name: '' });
  const [rescueBusy, setRescueBusy] = useState(false);
  const [rescueSent, setRescueSent] = useState(false);
  const [rescueHuman, setRescueHuman] = useState('');
  const [unlockHuman, setUnlockHuman] = useState('');
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ email: '', restaurant_name: '', phone: '' });
  const [unlocking, setUnlocking] = useState(false);
  const resultRef = useRef(null);
  const { search } = useLocation();
  const [weekScans, setWeekScans] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/index-ai/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.week_scans) setWeekScans(d.week_scans); })
      .catch(() => {});
  }, []);

  // Hide floating widgets (Rachel concierge / sales alert) while the scanner flow is active
  useEffect(() => {
    const active = phase === 'scanning' || phase === 'preview';
    document.body.classList.toggle('kx-scan-active', active);
    return () => document.body.classList.remove('kx-scan-active');
  }, [phase]);

  // Auto-start when arriving from the Home mini-scanner (/index-ai?scan=domain.com)
  const autoStarted = useRef(false);
  useEffect(() => {
    const q = new URLSearchParams(search).get('scan');
    if (q && !autoStarted.current) {
      autoStarted.current = true;
      setUrl(q);
      setTimeout(() => {
        document.getElementById('scan')?.scrollIntoView({ behavior: 'smooth' });
        runScan(q);
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (phase !== 'scanning') return;
    setStepIdx(0);
    const id = setInterval(() => setStepIdx((i) => Math.min(i + 1, t.steps.length - 1)), 900);
    return () => clearInterval(id);
  }, [phase, t.steps.length]);

  const runScan = async (target) => {
    if (!target.trim()) return;
    trackEvent('index_ai_started', { domain: target.trim() });
    trackEvent('free_audit_start', { source: 'index_ai', domain: target.trim() });
    setError('');
    setBlockedDomain('');
    setRescueSent(false);
    setData(null);
    setPhase('scanning');
    const started = Date.now();
    try {
      const r = await fetch(`${API}/api/index-ai/scan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: target.trim(), ref_code: getRef() || null, lang }),
      });
      if (!r.ok) {
        let code = 'unreachable';
        try { code = (await r.json())?.detail?.code || 'unreachable'; } catch { /* noop */ }
        setPhase('idle');
        if (code === 'blocked') {
          setBlockedDomain(target.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, ''));
        } else {
          setError(t.invalid);
        }
        return;
      }
      const d = await r.json();
      trackEvent('index_ai_completed', { domain: target.trim(), score: d?.total });
      const wait = Math.max(0, 5200 - (Date.now() - started));
      setTimeout(() => {
        setData(d);
        setPhase('preview');
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      }, wait);
    } catch {
      setPhase('idle');
      setError(t.invalid);
    }
  };

  const scan = (e) => {
    e.preventDefault();
    runScan(url);
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (!form.email || !form.restaurant_name) return;
    if (!unlockHuman) { setError(isEs ? 'Confirma que no eres un robot.' : "Please confirm you're not a robot."); return; }
    setError('');
    setUnlocking(true);
    try {
      const r = await fetch(`${API}/api/index-ai/scan/${data.scan_id}/unlock`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Human-Token': unlockHuman }, body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error('unlock');
      setData(await r.json());
      setPhase('unlocked');
    } catch {
      setError(t.invalid);
    } finally {
      setUnlocking(false);
    }
  };

  const verdict = data ? (data.total >= 80 ? t.verdictGood : data.total >= 55 ? t.verdictMid : t.verdictBad) : '';
  const verdictColor = data ? (data.total >= 80 ? 'text-emerald-600' : data.total >= 55 ? 'text-[#F77F11]' : 'text-red-500') : '';

  return (
    <section id="scan" className="py-16 lg:py-24 bg-gradient-to-b from-orange-50/60 to-white scroll-mt-28" data-testid="index-ai-scanner">
      <div className="max-w-[1080px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{t.eyebrow}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[44px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.08]">{t.title}</h2>
          <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{t.body}</p>
        </div>

        {/* Live social proof */}
        {weekScans && (
          <p className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-emerald-200 bg-emerald-50/80 px-4 py-2 text-[13px] font-semibold text-emerald-800" data-testid="scan-social-proof">
            <span className="relative flex w-2 h-2 shrink-0">
              <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-70" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
            </span>
            {isEs
              ? <>{weekScans} restaurantes escaneados esta semana — la mayoría descubre dónde les gana su competencia.</>
              : <>{weekScans} restaurants scanned this week — most discover exactly where their competitor beats them.</>}
          </p>
        )}

        {/* Input */}
        <form onSubmit={scan} className="mt-8 flex flex-col sm:flex-row gap-2 sm:gap-0 sm:items-center bg-white rounded-2xl sm:rounded-full border border-neutral-200 shadow-[0_24px_70px_-26px_rgba(247,127,17,0.5)] p-2 sm:pl-5 max-w-xl focus-within:border-[#F77F11]/60 transition-all">
          <Globe className="hidden sm:block w-4.5 h-4.5 w-5 h-5 text-neutral-400 shrink-0" />
          <input
            value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder={t.placeholder} inputMode="url" autoComplete="url"
            className="flex-1 h-11 px-4 sm:px-3 bg-transparent outline-none text-[15px] text-neutral-900 placeholder:text-neutral-400 min-w-0"
            data-testid="scan-url-input"
          />
          <button type="submit" disabled={phase === 'scanning'}
            className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white h-11 px-7 rounded-xl sm:rounded-full text-[14.5px] font-bold transition-colors"
            data-testid="scan-submit-btn">
            {phase === 'scanning' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {phase === 'scanning' ? t.scanning + '…' : t.cta}
          </button>
        </form>
        <p className="mt-2.5 text-[12.5px] text-neutral-400">{t.onlyRestaurants}</p>
        <p className="mt-2 text-[13.5px] text-neutral-500" data-testid="scan-talk-line">
          {isEs ? '¿Prefieres hablar con una persona?' : 'Prefer to talk to a human?'}{' '}
          <Link to="/demo" className="text-[#F77F11] font-bold hover:underline">{isEs ? 'Agenda tu demo gratis →' : 'Book your free demo →'}</Link>
          {' · '}
          <a href="https://wa.me/14085901102" target="_blank" rel="noopener noreferrer" className="text-[#F77F11] font-bold hover:underline">WhatsApp</a>
        </p>
        {error && <p className="mt-2 text-[13.5px] text-red-500 font-medium max-w-xl" data-testid="scan-error">{error}</p>}

        {/* Firewall-blocked rescue: turn the failed scan into a manual-audit lead */}
        <AnimatePresence>
          {blockedDomain && phase === 'idle' && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mt-5 max-w-xl rounded-2xl border-2 border-[#F77F11] bg-white shadow-[0_24px_70px_-30px_rgba(247,127,17,0.45)] p-6" data-testid="scan-blocked-rescue">
              {rescueSent ? (
                <div data-testid="scan-blocked-success">
                  <p className="text-[16px] font-bold text-neutral-900">{t.blockedDoneT}</p>
                  <p className="mt-1 text-[13.5px] text-neutral-600">{t.blockedDone}{' '}
                    <a href="https://wa.me/14085901102" target="_blank" rel="noopener noreferrer" className="text-[#F77F11] font-bold hover:underline">WhatsApp</a>
                    {' · '}
                    <Link to="/demo" className="text-[#F77F11] font-bold hover:underline">{isEs ? 'Agendar demo →' : 'Book a demo →'}</Link>
                  </p>
                </div>
              ) : (
                <>
                  <p className="flex items-center gap-2 text-[15.5px] font-bold text-neutral-900">
                    <ShieldAlert className="w-4.5 h-4.5 w-5 h-5 text-[#F77F11] shrink-0" /> {t.blockedTitle}
                  </p>
                  <p className="mt-1.5 text-[13.5px] text-neutral-600 leading-relaxed">{t.blockedBody}</p>
                  <HumanCheck className="mt-3" onToken={setRescueHuman} />
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!rescue.email.trim()) return;
                      if (!rescueHuman) { setError(isEs ? 'Confirma que no eres un robot.' : "Please confirm you're not a robot."); return; }
                      setError('');
                      setRescueBusy(true);
                      try {
                        const r = await fetch(`${API}/api/lead`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Human-Token': rescueHuman },
                          body: JSON.stringify({
                            email: rescue.email.trim(), source: 'blocked_scan', locale: isEs ? 'es' : 'en',
                            company: rescue.name.trim() || blockedDomain, interest: `Manual audit — firewall blocked (${blockedDomain})`,
                            source_page: '/index-ai#scan',
                            metadata: { domain: blockedDomain, ...(getRef() ? { ref: getRef() } : {}) },
                          }),
                        });
                        if (r.ok) setRescueSent(true);
                        else setError(t.invalid);
                      } catch { setError(t.invalid); }
                      setRescueBusy(false);
                    }}
                    className="mt-4 flex flex-col sm:flex-row gap-2.5">
                    <input required type="email" value={rescue.email} onChange={(e) => setRescue({ ...rescue, email: e.target.value })}
                      placeholder={t.blockedEmail} className="flex-1 h-11 rounded-xl border border-neutral-200 px-4 text-[14px] outline-none focus:border-[#F77F11] min-w-0 w-full" data-testid="blocked-email-input" />
                    <input value={rescue.name} onChange={(e) => setRescue({ ...rescue, name: e.target.value })}
                      placeholder={t.blockedName} className="flex-1 h-11 rounded-xl border border-neutral-200 px-4 text-[14px] outline-none focus:border-[#F77F11] min-w-0 w-full" data-testid="blocked-name-input" />
                    <button type="submit" disabled={rescueBusy}
                      className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white h-11 px-5 rounded-xl text-[13.5px] font-bold transition-colors" data-testid="blocked-submit-btn">
                      {rescueBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {rescueBusy ? t.blockedSending : t.blockedCta}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Scanning animation */}
        <AnimatePresence>
          {phase === 'scanning' && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mt-8 max-w-xl rounded-2xl border border-neutral-200 bg-white p-6" data-testid="scan-progress">
              <p className="text-[13px] font-bold text-neutral-900">{t.scanning} {url.replace(/^https?:\/\//, '')}…</p>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {t.steps.map((s, i) => (
                  <div key={s} className="flex items-center gap-2 text-[12.5px]">
                    {i < stepIdx
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      : i === stepIdx
                        ? <Loader2 className="w-4 h-4 text-[#F77F11] animate-spin shrink-0" />
                        : <span className="w-4 h-4 rounded-full border border-neutral-200 shrink-0" />}
                    <span className={i <= stepIdx ? 'text-neutral-800 font-medium' : 'text-neutral-400'}>{s}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results */}
        {data && phase !== 'scanning' && (
          <motion.div ref={resultRef} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mt-10 rounded-3xl border border-neutral-200 bg-white shadow-[0_40px_100px_-40px_rgba(0,0,0,0.15)] overflow-hidden scroll-mt-32" data-testid="scan-results">
            {/* Header: score + categories — blurred until the visitor leaves their email */}
            <div className="p-6 sm:p-9 grid md:grid-cols-[auto,1fr] gap-8 items-center border-b border-neutral-100 relative">
              <div className={`flex flex-col items-center text-center ${phase !== 'unlocked' ? 'blur-md select-none pointer-events-none' : ''}`} aria-hidden={phase !== 'unlocked'}>
                <p className="text-[10.5px] tracking-[0.22em] font-bold uppercase text-neutral-400">Kitxens Index AI Score™</p>
                <div className="mt-3"><ScoreRing score={data.total} /></div>
                <p className={`mt-2 text-[15px] font-bold ${verdictColor}`} data-testid="scan-verdict">{verdict}</p>
                <p className="mt-1 text-[12px] text-neutral-400">{data.domain}</p>
              </div>
              <div className={phase !== 'unlocked' ? 'blur-md select-none pointer-events-none' : ''} aria-hidden={phase !== 'unlocked'}>
                <CategoryBars data={data} isEs={isEs} />
              </div>
              {phase !== 'unlocked' && (
                <div className="absolute inset-0 flex items-center justify-center" data-testid="scan-score-gate">
                  <div className="rounded-2xl bg-white/90 backdrop-blur-sm border border-[#F77F11]/40 shadow-xl px-6 py-4 text-center max-w-sm mx-4">
                    <p className="flex items-center justify-center gap-2 text-[14.5px] font-bold text-neutral-900">
                      <Lock className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Tu informe está listo' : 'Your report is ready'}
                    </p>
                    <p className="mt-1 text-[12.5px] text-neutral-500">
                      {isEs ? 'Deja tu email abajo para revelar tu Index AI Score y el diagnóstico completo — gratis.' : 'Leave your email below to reveal your Index AI Score and the full diagnosis — free.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Checklist */}
            <div className="p-6 sm:p-9">
              {phase === 'unlocked' ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-[15px] font-bold text-neutral-900">{t.fullTitle}</p>
                    <a href={`${API}/api/index-ai/scan/${data.scan_id}/report`}
                      onClick={() => trackEvent('pdf_report_generated', { scan_id: data.scan_id })}
                      className="inline-flex items-center gap-2 rounded-full bg-neutral-950 hover:bg-neutral-800 text-white h-10 px-5 text-[13px] font-bold transition-colors"
                      data-testid="scan-pdf-btn">
                      <Download className="w-4 h-4" /> {t.pdf}
                    </a>
                  </div>
                  <p className="mt-2 text-[12.5px] text-neutral-500 flex items-center gap-1.5" data-testid="scan-emailed-note">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> {t.emailed}
                  </p>

                  {/* Summary strip */}
                  <div className="mt-5 grid grid-cols-3 gap-2.5 sm:gap-3" data-testid="scan-summary-strip">
                    <div className="rounded-2xl border border-neutral-200 bg-neutral-50 px-3 py-3.5 text-center">
                      <p className="text-2xl sm:text-3xl font-bold text-neutral-900 tabular-nums">{data.checks.length}</p>
                      <p className="mt-0.5 text-[11px] sm:text-[12px] font-semibold text-neutral-500">{t.statChecks}</p>
                    </div>
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 px-3 py-3.5 text-center">
                      <p className="text-2xl sm:text-3xl font-bold text-emerald-600 tabular-nums">{data.checks.filter((c) => c.ok).length}</p>
                      <p className="mt-0.5 text-[11px] sm:text-[12px] font-semibold text-emerald-700">{t.statPass}</p>
                    </div>
                    <div className="rounded-2xl border border-amber-200/70 bg-amber-50/60 px-3 py-3.5 text-center">
                      <p className="text-2xl sm:text-3xl font-bold text-amber-600 tabular-nums">{data.checks.filter((c) => !c.ok).length}</p>
                      <p className="mt-0.5 text-[11px] sm:text-[12px] font-semibold text-amber-700">{t.statIssues}</p>
                    </div>
                  </div>

                  {/* Category-grouped diagnosis */}
                  <div className="mt-7 space-y-7">
                    {Object.entries(data.cat_labels).map(([key, meta]) => {
                      const catChecks = data.checks.filter((c) => c.cat === key);
                      if (!catChecks.length) return null;
                      const val = data.categories[key] ?? 0;
                      const pct = (val / meta.max) * 100;
                      const color = pct >= 70 ? '#10b981' : pct >= 45 ? '#F77F11' : '#ef4444';
                      return (
                        <div key={key} data-testid={`scan-section-${key}`}>
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-[14px] font-bold text-neutral-900">{isEs ? meta.es : meta.en}</p>
                            <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-bold tabular-nums"
                              style={{ color, backgroundColor: `${color}14` }}>
                              {val} / {meta.max}
                            </span>
                          </div>
                          <div className="mt-2 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
                          </div>
                          <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
                            {catChecks.map((c) => <CheckRow key={c.id} c={c} isEs={isEs} showFix />)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Preliminary scope note */}
                  <div className="mt-8 rounded-2xl border border-[#F77F11]/40 bg-orange-50/70 p-5 sm:p-6" data-testid="scan-prelim-note">
                    <p className="flex items-center gap-2 text-[14px] font-bold text-neutral-900">
                      <AlertTriangle className="w-4 h-4 text-[#F77F11] shrink-0" /> {t.prelimT}
                    </p>
                    <p className="mt-1.5 text-[13.5px] text-neutral-600 leading-relaxed">{t.prelim}</p>
                  </div>

                  {/* Upsell */}
                  <div className="mt-5 rounded-2xl bg-neutral-950 text-white p-6 sm:p-8">
                    <p className="text-[17px] font-bold leading-snug">{t.upsellTitle}</p>
                    <p className="mt-2 text-neutral-400 text-[14px] leading-relaxed max-w-xl">{t.upsellBody}</p>
                    <p className="mt-3 text-[13.5px] font-semibold text-[#FF9B4A]" data-testid="scan-discount-line">{t.discount}</p>
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <Link to="/demo" className="inline-flex items-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white h-11 px-6 rounded-full text-[14px] font-bold transition-colors" data-testid="scan-consult-cta">
                        {t.upsellCta} <ArrowRight className="w-4 h-4" />
                      </Link>
                      <a href="https://wa.me/14085901102" target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 border border-white/25 hover:border-[#F77F11] text-white hover:text-[#FF9B4A] h-11 px-6 rounded-full text-[13.5px] font-semibold transition-colors"
                        data-testid="scan-whatsapp-cta">
                        {t.wa}
                      </a>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    {data.visible_checks.map((c) => <CheckRow key={c.id} c={c} isEs={isEs} />)}
                  </div>
                  {/* Locked area: blurred rows fill behind, unlock card flows normally (never clipped) */}
                  <div className="relative mt-2.5">
                    <div className="absolute inset-0 grid sm:grid-cols-2 gap-2.5 content-start select-none pointer-events-none overflow-hidden" aria-hidden="true">
                      {Array.from({ length: Math.min(8, data.locked_count) }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2.5 rounded-xl border border-neutral-200 bg-neutral-50 px-3.5 py-3 blur-[5px]">
                          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                          <span className="h-3 rounded bg-neutral-300" style={{ width: `${55 + (i * 13) % 40}%` }} />
                        </div>
                      ))}
                    </div>
                    {/* Unlock form in normal flow */}
                    <div className="relative flex items-center justify-center py-6 px-1">
                      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white/95 backdrop-blur-md shadow-2xl p-6" data-testid="scan-unlock-card">
                        <p className="flex items-center gap-2 text-[15px] font-bold text-neutral-900"><Lock className="w-4 h-4 text-[#F77F11]" /> {t.lockedTitle}</p>
                        <p className="mt-1.5 text-[13px] text-neutral-500 leading-relaxed">{t.lockedBody} · {t.issues(data.issues_found)}.</p>
                        <form onSubmit={unlock} className="mt-4 space-y-2.5">
                          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder={t.email} className="w-full h-11 rounded-xl border border-neutral-200 px-4 text-[14px] outline-none focus:border-[#F77F11]" data-testid="unlock-email-input" />
                          <input required value={form.restaurant_name} onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })}
                            placeholder={t.restaurant} className="w-full h-11 rounded-xl border border-neutral-200 px-4 text-[14px] outline-none focus:border-[#F77F11]" data-testid="unlock-restaurant-input" />
                          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder={t.phone} className="w-full h-11 rounded-xl border border-neutral-200 px-4 text-[14px] outline-none focus:border-[#F77F11]" data-testid="unlock-phone-input" />
                          <HumanCheck onToken={setUnlockHuman} />
                          {error && <p className="text-[12.5px] text-red-500" data-testid="unlock-error">{error}</p>}
                          <button type="submit" disabled={unlocking}
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white h-11 rounded-xl text-[14px] font-bold transition-colors" data-testid="unlock-submit-btn">
                            {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {unlocking ? t.unlocking : t.unlock}
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}

        {phase === 'unlocked' && <ShareEarnBar className="mt-4 rounded-3xl" />}
      </div>
    </section>
  );
}
